import React, { useEffect, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMedicalProfessionalsForMedicalCenter } from '~/services/medical-centers';
import { Pagination } from '../Pagination';
import AddProfessionalModal from '~/components/MedicalProfessionalDetail/modals/AddProfessional';

interface Professional {
  id: number;
  name: string;
  status: string;
  patients: number;
  referrals: number;
  report_number_of_patients: number;
}

const columnHelper = createColumnHelper<Professional>();

const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: () => 'Name',
    cell: (info) => (
      <Link to={`/medical-professionals/${info.row.original.id}`}>
        <div className='font-bold text-primary'>{info.renderValue()}</div>
      </Link>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      return (
        <div
          className={`inline-flex rounded-full px-3 py-1 text-sm hover:bg-opacity-90 ${
            info.getValue() === 'Active'
              ? 'bg-primary'
              : info.getValue() === 'Inactive'
              ? 'bg-whiten text-secondary'
              : ''
          }`}
        >
          {info.renderValue()}
        </div>
      );
    },
  }),
  columnHelper.accessor('patients', {
    header: () => <span>Patients</span>,
    cell: (info) => info.renderValue(),
  }),
  columnHelper.accessor('referrals', {
    header: 'Referrals',
    cell: (info) => info.renderValue(),
  }),
];

const ProfessionalsTab = ({ id, medCenter }: { id: number; medCenter: { id: number; name: string } }) => {
  const [data, setData] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const { data: result, isLoading, refetch } = useQuery({
    queryKey: [`medical_professionals-${id}`, page],
    queryFn: () => fetchMedicalProfessionalsForMedicalCenter(id, page),
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (result?.results?.length) {
      const mappedData = result?.results?.map((p: any) => ({
        id: p?.id,
        name: `${p?.person?.first_name} ${p?.person?.last_name}`,
        status: p?.is_active ? 'Active' : 'Inactive',
        patients: p?.report_number_of_patients,
        referrals: 'Need Data',
      }));
      setData(mappedData);
      const total = result?.count;
      if (total) {
        setPages(Math.ceil(total / 20));
      }
    }
  }, [result?.results]);

  return (
    <>
      <div className='flex justify-end mb-4'>
        <AddProfessionalModal
          initialMedCenter={medCenter}
          onCreated={() => refetch()}
        />
      </div>
      <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
        <thead className='border-separate px-4'>
          {isLoading
            ? Array.from({ length: 10 }).map((_, index) => (
                <tr key={index}>
                  {columns.map((_column, colIndex) => (
                    <td
                      key={colIndex}
                      className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                    >
                      <div className='max-w-sm animate-pulse'>
                        <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            : table.getHeaderGroups().map((headerGroup) => (
                <tr
                  className='border-t border-stroke bg-whiten'
                  style={{ fontSize: '14px' }}
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ paddingTop: '16px', paddingBottom: '16px' }}
                    >
                      <div className='flex items-center'>
                        <span>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </span>
                        <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                          <span className='inline-block'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='17'
                              height='16'
                              viewBox='0 0 17 16'
                              fill='none'
                            >
                              <path
                                d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                fill='#131212'
                              />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr className='border-t border-stroke' key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination page={page} setPage={setPage} pages={pages} totalCount={result?.count} />
    </>
  );
};

export default ProfessionalsTab;
